/**
 * Microsoft Clarity Analytics Integration
 * 
 * This utility initializes Microsoft Clarity tracking on the application.
 * To use Clarity, add REACT_APP_CLARITY_PROJECT_ID to your .env file.
 * 
 * @see https://clarity.microsoft.com/
 */

/**
 * Initialize Microsoft Clarity
 * @param {string} projectId - The Clarity project ID
 */
export const initializeClarity = (projectId) => {
  // Check if projectId is provided
  if (!projectId) {
    console.warn('Microsoft Clarity: Project ID not provided. Clarity tracking disabled.');
    return;
  }

  // Check if Clarity is already initialized
  if (window.clarity) {
    console.warn('Microsoft Clarity: Already initialized.');
    return;
  }

  try {
    // Create and configure the Clarity script
    (function(c, l, a, r, i, t, y) {
      c[a] = c[a] || function() { (c[a].q = c[a].q || []).push(arguments) };
      t = l.createElement(r);
      t.async = 1;
      t.src = 'https://www.clarity.ms/tag/' + i;
      y = l.getElementsByTagName(r)[0];
      y.parentNode.insertBefore(t, y);
    })(window, document, 'clarity', 'script', projectId);

    console.log('Microsoft Clarity: Initialized successfully with project ID:', projectId);
  } catch (error) {
    console.error('Microsoft Clarity: Failed to initialize', error);
  }
};

/**
 * Initialize Clarity from environment variables.
 * Disabled in development and unless REACT_APP_ENABLE_CLARITY=true.
 */
export const initializeClarityFromEnv = () => {
  if (process.env.NODE_ENV !== 'production') {
    return;
  }
  if (process.env.REACT_APP_ENABLE_CLARITY !== 'true') {
    return;
  }
  const projectId = process.env.REACT_APP_CLARITY_PROJECT_ID;
  if (projectId) {
    initializeClarity(projectId);
  }
};

/**
 * Check if Clarity is available
 * @returns {boolean} - True if Clarity is loaded
 */
export const isClarityAvailable = () => {
  return typeof window !== 'undefined' && typeof window.clarity !== 'undefined';
};

/**
 * Identify a user in Clarity
 * @param {string} userId - The user ID to identify
 */
export const identifyUser = (userId) => {
  if (!isClarityAvailable()) return;

  const sanitizedId = userId !== undefined && userId !== null
    ? String(userId)
    : '';

  if (sanitizedId.trim().length === 0) {
    return;
  }

  if (isClarityAvailable() && sanitizedId) {
    try {
      window.clarity('identify', sanitizedId);
    } catch (error) {
      console.error('Microsoft Clarity: Failed to identify user', error);
    }
  }
};

/**
 * Set a custom event in Clarity
 * @param {string} eventName - The event name
 * @param {object} eventData - Optional event data
 */
export const setEvent = (eventName, eventData = {}) => {
  if (isClarityAvailable() && eventName) {
    try {
      window.clarity('event', eventName, eventData);
    } catch (error) {
      console.error('Microsoft Clarity: Failed to set event', error);
    }
  }
};

